import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, concat, Subject, Subscription } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
  debounceTime,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { RfqService } from '@app/core/quotes';

@Component({
  selector: 'cnc-rfq-variant-skus-popup',
  templateUrl: './rfq-variant-skus-popup.component.html',
  styleUrls: ['./rfq-variant-skus-popup.component.scss'],
})
export class RfqVariantSkusPopupComponent implements OnInit {
  loading :boolean;

  @Input() page :any = null;
  @Input() parent :any = null;
  @Input() variant :any = null;
  @Input() selectParent :boolean = false;
  @Input() factoryId :number = null;
  @Input() sku :any = null;
  @Input() product_name :any = null;

  formData :UntypedFormGroup;
  submitted :boolean = false;

  pageTitle :string = 'Add Variant SKU';

  // sku
  rfq$ :Observable<any[]>;
  rfqLoading = false;
  rfqInput$ = new Subject<string>();

  constructor(
    public activeModal :NgbActiveModal,
    public rfqSvc :RfqService,
    private fb :UntypedFormBuilder,
    private translate :TranslateService,
    private toastr :ToastrService,
  ) {}

  ngOnInit() {
    this.loadRfqs();

    if (this.variant) {
      this.pageTitle = 'Edit Variant SKU';
      this.formData = this.fb.group({
        internal_sku: [this.variant.internal_sku, Validators.required],
        product_name: [this.variant.product_name, Validators.required],
        wholesale_price: [this.variant.wholesale_price],
        retail_price: [this.variant.retail_price],
        variant_parent_id: [this.variant.id],
      });
    } else {
      if (this.parent) {
        if (!this.parent.variant_skus) {
          this.parent.variant_skus = [];
        }

        this.formData = this.fb.group({
          parent_sku: [{ value: this.parent.internal_sku, disabled: true }],
          internal_sku: [
            this.parent.internal_sku + '-' + (this.parent.variant_skus.length + 1),
            Validators.required,
          ],
          product_name: [this.parent.product_name, Validators.required],
          wholesale_price: [this.parent.wholesale_price],
          retail_price: [this.parent.retail_price],
          variant_parent_id: [this.parent.id],
        });
      } else {
        this.formData = this.fb.group({
          parent_sku: [null],
          internal_sku: [this.sku !== null ? this.sku : '', Validators.required],
          product_name: [
            this.product_name !== null ? this.product_name : '',
            Validators.required,
          ],
          wholesale_price: [0],
          retail_price: [0],
          variant_parent_id: [0],
        });
      }
    }
  }

  private loadRfqs() {
    this.rfq$ = concat(
      of([]), // default items
      this.rfqInput$.pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        tap(() => (this.rfqLoading = true)),
        switchMap((term) =>
          this.rfqSvc
            .search({
              sku: term,
              parent_only: true,
              include_variants: true,
              factoryId: this.factoryId,
            })
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.rfqLoading = false)),
              map((resp) => resp),
            ),
        ),
      ),
    );
  }

  onSubmit() {
    this.submitted = true;
    if (this.formData.invalid) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }
    this.loading = true;
    console.log(this.formData.value);

    const data = { ...this.formData.value };

    if (this.variant) {
      this.rfqSvc.updateVariantSku(this.variant.id, data, this.page).subscribe(
        (res :any) => {
          this.loading = false;
          this.toastr.success('Update Successful!');
          this.activeModal.close();
        },
        (err) => {
          this.loading = false;
          Swal.fire({
            title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
            text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
            type: 'error',
            confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
          });
          this.activeModal.close();
        },
      );
    } else {
      this.rfqSvc.createVariantSku(data).subscribe(
        (res :any) => {
          this.loading = false;
          this.toastr.success('Save Successful!');
          this.activeModal.close(res.record);
        },
        (errorResp :any) => {
          if (
            errorResp.error?.error_type &&
            errorResp.error?.error_type === 'Existing'
          ) {
            Swal.fire({
              title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
              text: errorResp.error.message,
              type: 'error',
              confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
            }).then((data) => {
              this.loading = false;
              this.activeModal.close();
            });
          } else {
            Swal.fire({
              title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
              text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
              type: 'error',
              confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
            }).then((data) => {
              this.loading = false;
              this.activeModal.close();
            });
          }
        },
      );
    }
  }

  onSelectParent(parent) {
    console.log(parent);
    if (!parent.variant_skus) {
      parent.variant_skus = [];
    }

    this.formData = this.fb.group({
      parent_sku: [{ ...parent, value: parent.internal_sku, disabled: true }],
      internal_sku: [
        parent.internal_sku + '-' + (parent.variant_skus.length + 1),
        Validators.required,
      ],
      product_name: [parent.product_name, Validators.required],
      wholesale_price: [parent.wholesale_price],
      retail_price: [parent.retail_price],
      variant_parent_id: [parent.id],
    });

    console.log(this.parent);
  }
}
